import { FormControl, FormErrorMessage, Text } from '@chakra-ui/react';
import classNames from 'classnames';
import { type Dispatch, type SetStateAction, useId, useMemo } from 'react';
import NumberFormat, {
  type NumberFormatProps,
  type NumberFormatValues,
} from 'react-number-format';
import type { SingleValue } from 'react-select';

import { Input, type InputProps, Select } from '@revelio/ui';

import { useCurrencyPreference } from '../../../../../shared/user-preferences/hooks/useCurrencyPreference';
import { FILTER_FORMAT_MAP } from '../../engine/filterRegistry';
import { FilterId } from '../../types/filters';
import styles from '../filter-overlay-select.module.css';
import layoutStyles from './sub-filter-input-form.module.css';

interface OperatorOption {
  value: 'between' | 'greater_than' | 'less_than';
  label: string;
}

interface SubFilterInputFormProps {
  filterId: FilterId;
  startValue: number | undefined;
  setStartValue: Dispatch<SetStateAction<number | undefined>>;
  endValue: number | undefined;
  setEndValue: Dispatch<SetStateAction<number | undefined>>;
  operator: OperatorOption;
  setOperator: (operator: OperatorOption) => void;
  errorMessages?: string[];
  showErrors?: boolean;
  onShowErrorsChange?: (show: boolean) => void;
}

const operatorOptions: OperatorOption[] = [
  { value: 'between', label: 'is between' },
  { value: 'greater_than', label: 'is greater than' },
  { value: 'less_than', label: 'is less than' },
];

export const SubFilterInputForm = ({
  filterId,
  startValue,
  setStartValue,
  endValue,
  setEndValue,
  operator,
  setOperator,
  errorMessages = [],
  showErrors = false,
  onShowErrorsChange,
}: SubFilterInputFormProps) => {
  const { currencySymbol } = useCurrencyPreference();
  const format = FILTER_FORMAT_MAP[filterId];
  const isFundingFilter =
    filterId === FilterId.LAST_FUNDING_AMOUNT ||
    filterId === FilterId.TOTAL_FUNDING_AMOUNT;

  const sharedNumberFormatProps = useMemo(() => {
    if (
      filterId === FilterId.GRADUATION_YEAR ||
      filterId === FilterId.LAST_FUNDING_YEAR ||
      filterId === FilterId.YEAR_FOUNDED
    ) {
      return {
        thousandSeparator: false,
        format: '####',
        mask: '_',
      };
    }
    const prefix = (() => {
      if (!format || format.type !== 'prefix') return '';
      return format.text === '$' ? currencySymbol : format.text;
    })();
    const suffix = format?.type === 'suffix' ? format.text : '';
    return {
      thousandSeparator: true,
      ...(prefix && { prefix }),
      ...(suffix && { suffix }),
      ...(isFundingFilter && { decimalScale: 2, allowNegative: false }),
    };
  }, [filterId, currencySymbol, format, isFundingFilter]);

  const isInvalid = showErrors && errorMessages.length > 0;

  const errorId = useId();

  const handleOperatorChange = (value: SingleValue<OperatorOption>) => {
    if (value) {
      setOperator(value);
    }
    onShowErrorsChange?.(false);
  };

  return (
    <div className={layoutStyles.form}>
      <div data-testid="range-operator-select">
        <Select
          options={operatorOptions}
          value={operator}
          onChange={handleOperatorChange}
          size="sm"
          isSearchable={false}
          className={classNames(styles.overlaySelect, styles.operatorSelect)}
          classNamePrefix="filter-overlay-select"
        />
      </div>

      <FormControl isInvalid={isInvalid}>
        {operator.value === 'between' ? (
          <div className={layoutStyles.inputRow}>
            <NumberInput
              isInvalid={isInvalid}
              aria-describedby={isInvalid ? errorId : undefined}
              {...sharedNumberFormatProps}
              value={startValue}
              onValueChange={(values: NumberFormatValues) => {
                setStartValue(values.floatValue ?? undefined);
                onShowErrorsChange?.(false);
              }}
              data-testid="range-num-input-start"
            />
            <Text>to</Text>
            <NumberInput
              isInvalid={isInvalid}
              aria-describedby={isInvalid ? errorId : undefined}
              {...sharedNumberFormatProps}
              value={endValue}
              onValueChange={(values: NumberFormatValues) => {
                setEndValue(values.floatValue ?? undefined);
                onShowErrorsChange?.(false);
              }}
              data-testid="range-num-input-end"
            />
          </div>
        ) : (
          <NumberInput
            isInvalid={isInvalid}
            aria-describedby={isInvalid ? errorId : undefined}
            key={`single-${operator.value}`}
            {...sharedNumberFormatProps}
            value={operator.value === 'greater_than' ? startValue : endValue}
            onValueChange={
              operator.value === 'greater_than'
                ? (values: NumberFormatValues) => {
                    setStartValue(values.floatValue ?? undefined);
                    onShowErrorsChange?.(false);
                  }
                : (values: NumberFormatValues) => {
                    setEndValue(values.floatValue ?? undefined);
                    onShowErrorsChange?.(false);
                  }
            }
            data-testid={
              operator.value === 'greater_than'
                ? 'range-num-input-start'
                : 'range-num-input-end'
            }
          />
        )}

        <FormErrorMessage id={errorId}>
          <div className={layoutStyles.errorList}>
            {errorMessages.map((msg, idx) => (
              <Text key={idx} color="red.600">
                {msg}
              </Text>
            ))}
          </div>
        </FormErrorMessage>
      </FormControl>
    </div>
  );
};

const NumberInput = ({
  value,
  ...restProps
}: NumberFormatProps<InputProps> & {
  value?: number | string | undefined;
}) => {
  const normalizedValue = value == null ? '' : value;

  return (
    <NumberFormat<InputProps>
      customInput={Input}
      size="sm"
      wrapperClassName={layoutStyles.numberField}
      thousandSeparator
      displayType="input"
      value={normalizedValue}
      {...restProps}
    />
  );
};
